import { PureComponent } from 'preact-compat'
import { ContentType, getConsistentContentType } from '@insided/shared/utils/contentType'
import {
  getPrivateId,
  getTopicByUrl,
  moveTopicToExistingTopic,
  moveRepliesToExistingTopic
} from '../services/topicService'
import Button from '@insided/control/uikit/Button'
import { Topic } from '../interfaces'

/* eslint-disable import/no-internal-modules */
import Modal, {
  ModalHeader,
  ModalBody,
  ModalFooter
} from '@insided/deprecated-preact-components/design-system-components/modal'
import InputUnit from '@insided/deprecated-preact-components/design-system-components/input-unit'
import TextField from '@insided/deprecated-preact-components/design-system-components/text-field'

export interface MoveToTopicModalProps {
  replyId?: number
  topicId?: number
  selectedReplies?: string[]
  moveType: string
  contentType: ContentType
  onClose: () => void
  onShowAlert: (type: 'success' | 'error', message?: string) => void
  getReplies?: (page?: number, pageSize?: number, sort?: string) => Promise<any>
  setSelectedReplies?: (selectedReplies: string[]) => void
  publicId?: string
  visibleReplyCount?: number | null
}

export interface MoveToTopicModalState {
  isLoading: boolean
  isUrlValid: boolean
  error: string | null
  topic: Partial<Topic> | null
}

function getSuccessMessage(multipleReplies: boolean, topic: Partial<Topic>) {
  const topicUrl = `${window.location.origin}${topic.url?.split('.com')[1]}`
  const topicLink = `<a href="${topicUrl}">${topic.title}</a>`
  return `The ${multipleReplies ? 'replies have' : 'reply has'} been successfully moved to ${topicLink}`
}

class MoveToExistingTopicModal extends PureComponent<MoveToTopicModalProps, MoveToTopicModalState> {
  constructor(props: MoveToTopicModalProps) {
    super(props)
    this.onMoveReplyToExistingTopic = this.onMoveReplyToExistingTopic.bind(this)
    this.onMoveTopicToExistingTopic = this.onMoveTopicToExistingTopic.bind(this)
    this.checkUrl = this.checkUrl.bind(this)
    this.state = {
      isLoading: false,
      isUrlValid: false,
      topic: null,
      error: null
    }
  }

  async checkUrl(topicUrl: string) {
    const { publicId, contentType, visibleReplyCount } = this.props
    try {
      const topic = await getTopicByUrl(topicUrl)
      let error = null

      if (publicId && topic && parseInt(publicId) === topic.threadid) {
        error = 'Unable to move this content. Please make sure the origin and destination are not the same.'
      } else if (visibleReplyCount) {
        error = `${contentType} with visible replies cannot be converted to a reply. Please first trash or move the replies and try again.`
      }

      this.setState({
        topic: {
          id: topic.threadid,
          title: topic.topictitle,
          contentType: getConsistentContentType(topic.contenttype),
          url: topic.url
        },
        isUrlValid: true,
        error: error
      })
    } catch (e) {
      this.setState({
        error: 'No topic found for this url, please try something else.',
        isUrlValid: false,
        topic: null
      })
    }
  }

  async onMoveTopicToExistingTopic() {
    const { contentType, topicId, onShowAlert } = this.props
    const { topic } = this.state

    if (!topicId || !topic) {
      return
    }

    this.setState({ isLoading: true })

    try {
      const privateId = await getPrivateId(Number(topic.id))
      await moveTopicToExistingTopic(contentType, topicId, privateId, topic.contentType as ContentType)
      window.location.assign(`${window.location.origin}${topic.url?.split('.com')[1]}`)
    } catch {
      onShowAlert('error')
    }
    this.setState({ isLoading: false, isUrlValid: false })
  }

  async onMoveReplyToExistingTopic() {
    const { contentType, replyId, onShowAlert, onClose, selectedReplies, getReplies, setSelectedReplies } = this.props
    const { topic } = this.state

    const replyIds = replyId ? [String(replyId)] : selectedReplies
    if (!replyIds || !topic || typeof getReplies !== 'function') {
      return
    }

    this.setState({ isLoading: true })

    try {
      const privateId = await getPrivateId(Number(topic.id))
      await moveRepliesToExistingTopic(contentType, replyIds, privateId, topic.contentType as ContentType)
      onClose()
      onShowAlert('success', getSuccessMessage(replyIds.length > 1, topic))
      setTimeout(async () => {
        await getReplies()
      }, 500)
      if (!replyId && typeof setSelectedReplies === 'function') {
        setSelectedReplies([])
      }
    } catch {
      onShowAlert('error')
    }
    this.setState({ isLoading: false })
  }

  render() {
    const { onClose, moveType, topicId } = this.props
    const { isLoading, error, topic, isUrlValid } = this.state

    return (
      <Modal onClose={onClose} isControl>
        <ModalHeader title={moveType} />
        <ModalBody>
          <InputUnit
            label='Enter the link of the topic you want to move the comment to:'
            isControl
            error={error ?? undefined}
          >
            <TextField
              id='move-to-topic'
              ariaLabel='move-to-topic'
              name='move-to-topic'
              isControl
              placeholder='http://'
              onKeyUp={this.checkUrl}
              type='text'
            />
          </InputUnit>
          {topic && isUrlValid && (
            <div className='valid-url-message'>
              <InputUnit label={`Topic: ${topic.title} `}>
                <div>{`You are about to relocate this reply to the topic: ${topic.title}. The position of your post will be based on the date and time it was originally posted. Are you sure you want to do this?`}</div>
              </InputUnit>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            title={moveType}
            variant='primary'
            onClick={topicId ? this.onMoveTopicToExistingTopic : this.onMoveReplyToExistingTopic}
            isLoading={isLoading}
            disabled={!isUrlValid || !!error}
            size='medium'
            data-qa={moveType}
          />
        </ModalFooter>
      </Modal>
    )
  }
}

export default MoveToExistingTopicModal
